import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './preview.css'; 
import Nav from './nav';
import Footer from './footer';
import Top from './top';

const baseURL = process.env.REACT_APP_API_BASE_URL;

function Product_Preview() {
  const { designId } = useParams();
  
  // ====================== STATES ======================
  const [design, setDesign] = useState(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [mainImage, setMainImage] = useState(''); // dynamic main image

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedPaperType, setSelectedPaperType] = useState('');
  const [selectedPaperWeight, setSelectedPaperWeight] = useState('');
  const [selectedFinish, setSelectedFinish] = useState('');
  const [selectedCorner, setSelectCorner] = useState('');
  const [selectedSide, setSelectedSide] = useState('');
  const [selectedInnerPage, setSelectedInnerPage] = useState('');
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedPadMaterial, setSelectedPadMaterial] = useState('');
  const [selectBinding, setSelectedBinding] = useState('');
  const [selectedPageLayout, setSelectedPageLayout] = useState('');
  const [selectedCover, setSelectedCover] = useState('');
  const [quantity, setQuantity] = useState(100);
  const [finalPrice, setFinalPrice] = useState(0);

  const [quantityError, setQuantityError] = useState('');
  const [pageLayoutError, setPageLayoutError] = useState('');
  const [coverError, setCoverError] = useState('');

  // ====================== HELPERS ======================
  const parseJSON = (json) => {
    try { return JSON.parse(json); } 
    catch { return {}; }
  };

  // ====================== FETCH DESIGN ======================
  useEffect(() => {
    const fetchDesign = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/preview/${designId}`);
        if (response.data.designs && response.data.designs.length > 0) {
          const fetchedDesign = response.data.designs[0];
          setDesign(fetchedDesign);
          setMainImage(`${baseURL}/images/${fetchedDesign.image}`); // set default image
        }
      } catch (error) {
        console.log('Error fetching design:', error.message);
      }
    };
    fetchDesign();
  }, [designId]);

  // ====================== PRICE CALCULATION ======================
  useEffect(() => {
    if (!design) return;
    let price = 0;

    const parse = (data) => {
      const json = parseJSON(data);
      return typeof json === 'object' ? json : {};
    };

    const getPrice = (obj, key) => {
      if (!key || !obj[key]) return 0;
      return typeof obj[key] === 'object' ? (obj[key].price || 0) : obj[key];
    };

    // parse all JSON fields
    const sizePrice = parse(design.size_price);
    const paperTypePrice = parse(design.paper_type_price);
    const paperWeightPrice = parse(design.paper_weight_price);
    const finishPrice = parse(design.finish_price);
    const cornerPrice = parse(design.corner);
    const innerPagePrice = parse(design.inner_paper);
    const pagePrice = parse(design.page);
    const padMaterialPrice = parse(design.pad_material);
    const bindingPrice = parse(design.binding);
    const colorPrice = parse(design.color);
    const sidePrice = parse(design.side);
    const pageLayoutPrice = parse(design.page_layout);
    const coverPrice = parse(design.cover);

    // apply pricing
    price += getPrice(sizePrice, selectedSize);
    price += getPrice(paperTypePrice, selectedPaperType);
    price += getPrice(paperWeightPrice, selectedPaperWeight);
    price += getPrice(finishPrice, selectedFinish);
    price += getPrice(cornerPrice, selectedCorner);
    price += getPrice(sidePrice, selectedSide);
    price += getPrice(innerPagePrice, selectedInnerPage);
    price += getPrice(pagePrice, selectedPage);
    price += getPrice(padMaterialPrice, selectedPadMaterial);
    price += getPrice(bindingPrice, selectBinding);
    price += getPrice(colorPrice, selectedColor);
    price += getPrice(pageLayoutPrice, selectedPageLayout);
    price += getPrice(coverPrice, selectedCover);

    price = price * (quantity || 1);
    setFinalPrice(price);
  }, [
    design, selectedSize, selectedPaperType, selectedPaperWeight,
    selectedFinish, selectedCorner, selectedSide, selectedColor,
    selectedInnerPage, selectedPage, selectedPadMaterial,
    selectBinding, selectedPageLayout, selectedCover, quantity
  ]);

  // ====================== FILE HANDLING ======================
  useEffect(() => {
    const objectUrls = files.map(file => URL.createObjectURL(file));
    setPreviews(objectUrls);
    return () => objectUrls.forEach(url => URL.revokeObjectURL(url));
  }, [files]);

  const handleFileChange = (e) => {
    setFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (selectedPageLayout.length < 2) {
      setPageLayoutError('Page layout selection is too short');
      return;
    } else setPageLayoutError('');

    if (selectedCover.length < 2) {
      setCoverError('Cover selection is too short');
      return;
    } else setCoverError('');

    const formData = new FormData();
    files.forEach(file => formData.append('files[]', file));

    axios.post(`${baseURL}/api/upload`, formData)
      .then(() => alert('Files uploaded successfully!'))
      .catch(err => console.log(err));
  };

  if (!design) return <div>Loading...</div>;

  // ====================== OPTIONS ======================
  const sizeOptions = parseJSON(design.size_price);
  const paperTypeOptions = parseJSON(design.paper_type_price);
  const paperWeightOptions = parseJSON(design.paper_weight_price);
  const finishOptions = parseJSON(design.finish_price);
  const cornerOptions = parseJSON(design.corner);
  const sideOptions = parseJSON(design.side);
  const innerPageOptions = parseJSON(design.inner_paper);
  const pageOptions = parseJSON(design.page);
  const padMaterialOptions = parseJSON(design.pad_material);
  const bindingOptions = parseJSON(design.binding);
  const colorOptions = parseJSON(design.color);
  const pageLayoutOptions = parseJSON(design.page_layout);
  const coverOptions = parseJSON(design.cover);

  // ====================== MAIN RETURN ======================
  return (
    <>
      <Nav />
      <Top />
      <div className="container mt-4 mb-10">
        <div className="row mb-4">

         {/* LEFT: Main Image + Gallery */}
<div className="col-md-6 text-center">
  {/* Main Image */}
  <img
    src={mainImage || `${baseURL}/images/${design.image}`}
    alt={design.dname}
    className="img-fluid rounded main-preview-image"
    style={{
      width: '100%',
      borderRadius: '10px',
      marginTop: '10px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      transition: '0.3s ease-in-out'
    }}
    onError={(e) => (e.target.src = `${baseURL}/images/${design.image}`)}
  />

  {/* Gallery Thumbnails */}
  <div
    className="image-gallery mt-3 d-flex flex-wrap justify-content-center gap-2"
    style={{ maxWidth: '100%' }}
  >
    {/* Default design image */}
    <img
      src={`${baseURL}/images/${design.image}`}
      alt="Default"
      onClick={() => setMainImage(`${baseURL}/images/${design.image}`)}
      className={`rounded border ${
        mainImage === `${baseURL}/images/${design.image}`
          ? 'border-primary'
          : 'border-secondary'
      }`}
      style={{
        width: '80px',
        height: '80px',
        objectFit: 'cover',
        cursor: 'pointer',
        borderWidth: '2px',
        transition: '0.2s ease-in-out',
      }}
    />

    {/* Gallery images from options (size, finish, corner, etc.) */}
    {[
      design.size_price,
      design.paper_type_price,
      design.paper_weight_price,
      design.finish_price,
      design.corner,
      design.side,
      design.inner_paper,
      design.page,
      design.pad_material,
      design.binding,
      design.color,
      design.page_layout,
      design.cover
    ].flatMap((json) => {
      const data = parseJSON(json);
      return Object.values(data)
        .filter((opt) => opt && opt.image)
        .map((opt, index) => (
          <img
            key={opt.image + index}
            src={`${baseURL}/images/${opt.image}`}
            alt="Gallery"
            onClick={() => setMainImage(`${baseURL}/images/${opt.image}`)}
            className={`rounded border ${
              mainImage === `${baseURL}/images/${opt.image}`
                ? 'border-primary'
                : 'border-secondary'
            }`}
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'cover',
              cursor: 'pointer',
              borderWidth: '2px',
              transition: '0.2s ease-in-out',
            }}
            onError={(e) => (e.target.style.display = 'none')}
          />
        ));
    })}
  </div>
</div>


          {/* RIGHT: Product Options */}
          <div className="col-md-5">
            <h2>{design.dname}</h2>
            <hr />
            <p style={{ fontSize: '11pt' }}>{design.ds1}</p>

            {/* ================= SIZE ================= */}
            {Object.keys(sizeOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Size:</label>
    <div className="col-sm-8">
      
    {designId == 26 ? (
  // ✅ CASE 1: If designId = 26 → show clickable div buttons
  <div className="d-flex gap-2 flex-wrap">
    {Object.entries(sizeOptions).map(([size, { price, image }]) => (
      <div
        key={size}
        className={`option-box d-flex align-items-center justify-content-center px-3 py-2 rounded border ${
          selectedSize === size ? 'border-primary bg-warning' : 'border-secondary'
        }`}
        style={{
          cursor: 'pointer',
          transition: '0.2s ease',
          borderWidth: '2px',
          minWidth: '70px',
          fontWeight: '500',
        }}
        onClick={() => {
          setSelectedSize(size);
          if (image) {
            setMainImage(`${baseURL}/images/${image}`);
          }
        }}
      >
        {size}
      </div>
    ))}
  </div>
) : (
        // ✅ CASE 2: If did ≠ 26 → show dropdown
        <select
          className="form-control"
          value={selectedSize}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedSize(value);
            if (sizeOptions[value]?.image) {
              setMainImage(`${baseURL}/images/${sizeOptions[value].image}`);
            }
          }}
        >
          <option value="">Select Size</option>
          {Object.entries(sizeOptions).map(([key]) => (
            <option key={key} value={key}>{key}</option>
          ))}
        </select>
      )}

    </div>
  </div>
)}


{/* ================= PAPER TYPE ================= */}
{Object.keys(paperTypeOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Type:</label>
    <div className="col-sm-8">
      <select
        className="form-control"
        value={selectedPaperType}
        onChange={(e) => {
          const value = e.target.value;
          setSelectedPaperType(value);
          if (paperTypeOptions[value]?.image) {
            setMainImage(`${baseURL}/images/${paperTypeOptions[value].image}`);
          }
        }}
      >
        <option value="">Select Type</option>
        {Object.entries(paperTypeOptions).map(([key]) => (
          <option key={key} value={key}>{key}</option>
        ))}
      </select>
    </div>
  </div>
)}

{/* ================= PAPER WEIGHT ================= */}
{Object.keys(paperWeightOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Paper Weight:</label>
    <div className="col-sm-8">
      <select
        className="form-control"
        value={selectedPaperWeight}
        onChange={(e) => {
          const value = e.target.value;
          setSelectedPaperWeight(value);
          if (paperWeightOptions[value]?.image) {
            setMainImage(`${baseURL}/images/${paperWeightOptions[value].image}`);
          }
        }}
      >
        <option value="">Select Paper Weight</option>
        {Object.entries(paperWeightOptions).map(([key]) => (
          <option key={key} value={key}>{key}</option>
        ))}
      </select>
    </div>
  </div>
)}

{/* ================= INNER PAPER ================= */}
{Object.keys(innerPageOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Inner Paper:</label>
    <div className="col-sm-8">
      <select
        className="form-control"
        value={selectedInnerPage}
        onChange={(e) => {
          const value = e.target.value;
          setSelectedInnerPage(value);
          if (innerPageOptions[value]?.image) {
            setMainImage(`${baseURL}/images/${innerPageOptions[value].image}`);
          }
        }}
      >
        <option value="">Select Inner Paper</option>
        {Object.entries(innerPageOptions).map(([key]) => (
          <option key={key} value={key}>{key}</option>
        ))}
      </select>
    </div>
  </div>
)}

{/* ================= PAGES ================= */}
{Object.keys(pageOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Pages:</label>
    <div className="col-sm-8">
      <select
        className="form-control"
        value={selectedPage}
        onChange={(e) => {
          const value = e.target.value;
          setSelectedPage(value);
          if (pageOptions[value]?.image) {
            setMainImage(`${baseURL}/images/${pageOptions[value].image}`);
          }
        }}
      >
        <option value="">Select Pages</option>
        {Object.entries(pageOptions).map(([key]) => (
          <option key={key} value={key}>{key}</option>
        ))}
      </select>
    </div>
  </div>
)}

{/* ================= PAD MATERIAL ================= */}
{Object.keys(padMaterialOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Pad Material:</label>
    <div className="col-sm-8">
      <select
        className="form-control"
        value={selectedPadMaterial}
        onChange={(e) => {
          const value = e.target.value;
          setSelectedPadMaterial(value);
          if (padMaterialOptions[value]?.image) {
            setMainImage(`${baseURL}/images/${padMaterialOptions[value].image}`);
          }
        }}
      >
        <option value="">Select Pad Material</option>
        {Object.entries(padMaterialOptions).map(([key]) => (
          <option key={key} value={key}>{key}</option>
        ))}
      </select>
    </div>
  </div>
)}

{/* ================= SIDE ================= */}
{Object.keys(sideOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Side:</label>
    <div className="col-sm-8 d-flex flex-wrap gap-3">
      {Object.entries(sideOptions).map(([key, value]) => (
        <div
          key={key}
          className={`option-box d-flex align-items-center gap-2 px-3 py-2 rounded border ${
            selectedSide === key ? 'border-primary' : 'border-secondary'
          }`}
          style={{
            cursor: 'pointer',
            transition: '0.2s ease',
            borderWidth: '2px',
            backgroundColor: selectedSide === key ? '#fadd71' : 'transparent',
          }}
          onClick={() => {
            setSelectedSide(key);
            if (value?.image) {
              setMainImage(`${baseURL}/images/${value.image}`);
            }
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: '500' }}>{key}</div>
        </div>
      ))}
    </div>
  </div>
)}

{/* ================= FINISH ================= */}
{Object.keys(finishOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Finish:</label>
    <div className="col-sm-8 d-flex flex-wrap gap-3">
      {Object.entries(finishOptions).map(([key, value]) => (
        <div
          key={key}
          className={`option-box d-flex align-items-center gap-2 px-3 py-2 rounded border ${
            selectedFinish === key ? 'border-primary' : 'border-secondary'
          }`}
          style={{
            cursor: 'pointer',
            transition: '0.2s ease',
            borderWidth: '2px',
            backgroundColor: selectedFinish === key ? '#fadd71' : 'transparent',
          }}
          onClick={() => {
            setSelectedFinish(key);
            if (value?.image) {
              setMainImage(`${baseURL}/images/${value.image}`);
            }
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: '500' }}>{key}</div>
        </div>
      ))}
    </div>
  </div>
)}

{/* ================= CORNER ================= */}
{Object.keys(cornerOptions).length > 0 && (
  <div className="form-group row align-items-center mt-3">
    <label className="col-sm-4">Corner:</label>

    <div className="col-sm-8 d-flex flex-wrap gap-3">
      {Object.entries(cornerOptions).map(([key, value]) => (
        <div
          key={key}
          onClick={() => {
            setSelectCorner(key);
            if (value?.image) {
              setMainImage(`${baseURL}/images/${value.image}`);
            }
          }}
          className={`corner-option text-center ${
            selectedCorner === key ? "border-primary" : "border-secondary"
          }`}
          style={{
            cursor: "pointer",
            borderWidth: "2px",
            borderStyle: "solid",
            padding: "10px",
            borderRadius: "10px",
            backgroundColor:
              selectedCorner === key ? "#fadd71" : "transparent",
            transition: "0.2s ease-in-out",
            boxShadow:
              selectedCorner === key
                ? "0 2px 8px rgba(0,0,0,0.2)"
                : "0 1px 3px rgba(0,0,0,0.1)",
            display: "flex",
            // flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "120px",
          }}
        >
          {/* Corner name */}
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              marginBottom: "5px",
              color: selectedCorner === key ? "#000" : "#444",
            }}
          >
            {key}
          </div>

          {/* SVG Icons */}
          {key.toLowerCase().includes("round") ? (
            <svg
              viewBox="0 0 48 48"
              className="corner-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              fill={selectedCorner === key ? "#000" : "#888"}
            >
              <path d="M9.5 34h-3v7.5H15v-3H9.5V34zm-3-14h3v9h-3zM20 38.5h8v3h-8zM8 6.5H6.5V15h3V9.537A30.542 30.542 0 0 1 38.463 38.5H33v3h8.5V40A33.538 33.538 0 0 0 8 6.5z"></path>
            </svg>
            
          ) : (
            <svg
              viewBox="0 0 48 48"
              className="corner-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              fill={selectedCorner === key ? "#000" : "#888"}
            >
              <path d="M6.5 6.5V15h3V9.5h29v29H33v3h8.5v-35h-35zm3 27.5h-3v7.5H15v-3H9.5V34zm-3-14h3v9h-3zM20 38.5h8v3h-8z"></path>
            </svg>
          )}
        </div>
      ))}
    </div>
  </div>
)}


{/* ================= COVER ================= */}
{Object.keys(coverOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Cover:</label>
    <div className="col-sm-8 d-flex flex-wrap gap-3">
      {Object.entries(coverOptions).map(([key, value]) => (
        <div
          key={key}
          className={`option-box d-flex align-items-center gap-2 px-3 py-2 rounded border ${
            selectedCover === key ? 'border-primary' : 'border-secondary'
          }`}
          style={{
            cursor: 'pointer',
            transition: '0.2s ease',
            borderWidth: '2px',
            backgroundColor: selectedCover === key ? '#fadd71' : 'transparent',
          }}
          onClick={() => {
            setSelectedCover(key);
            if (value?.image) {
              setMainImage(`${baseURL}/images/${value.image}`);
            }
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: '500' }}>{key}</div>
        </div>
      ))}
    </div>
  </div>
)}

{/* ================= PAGE LAYOUT ================= */}
{Object.keys(pageLayoutOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Page Layout:</label>
    <div className="col-sm-8 d-flex flex-wrap gap-3">
      {Object.entries(pageLayoutOptions).map(([key, value]) => (
        <div
          key={key}
          className={`option-box d-flex align-items-center gap-2 px-3 py-2 rounded border ${
            selectedPageLayout === key ? 'border-primary' : 'border-secondary'
          }`}
          style={{
            cursor: 'pointer',
            transition: '0.2s ease',
            borderWidth: '2px',
            backgroundColor: selectedPageLayout === key ? '#fadd71' : 'transparent',
          }}
          onClick={() => {
            setSelectedPageLayout(key);
            if (value?.image) {
              setMainImage(`${baseURL}/images/${value.image}`);
            }
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: '500' }}>{key}</div>
        </div>
      ))}
    </div>
  </div>
)}

{/* ================= BINDING ================= */}
{Object.keys(bindingOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Binding:</label>
    <div className="col-sm-8 d-flex flex-wrap gap-3">
      {Object.entries(bindingOptions).map(([key, value]) => (
        <div
          key={key}
          className={`option-box d-flex align-items-center gap-2 px-3 py-2 rounded border ${
            selectBinding === key ? 'border-primary' : 'border-secondary'
          }`}
          style={{
            cursor: 'pointer',
            transition: '0.2s ease',
            borderWidth: '2px',
            backgroundColor: selectBinding === key ? '#fadd71' : 'transparent',
          }}
          onClick={() => {
            setSelectedBinding(key);
            if (value?.image) {
              setMainImage(`${baseURL}/images/${value.image}`);
            }
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: '500' }}>{key}</div>
        </div>
      ))}
    </div>
  </div>
)}
{/* Color Selection */}
{Object.keys(colorOptions).length > 0 && (
  <div className="form-group row align-items-center">
    <label className="col-sm-4">Color:</label>
    <div className="col-sm-8 d-flex gap-3 flex-wrap">
      {Object.entries(colorOptions).map(([color, { price, image }], index) => (
        index === 0 ? (
          <button
            key={color}
            type="button"
            className="btn btn-outline-primary text-black"
            onClick={() => {
              setSelectedColor(color);
              setMainImage(`${baseURL}/images/${image}`); // ✅ FIXED
            }}
          >
            {color}
          </button>
        ) : (
          <div
            key={color}
            className={`color-circle ${selectedColor === color ? 'border-primary' : 'border-secondary'}`}
            onClick={() => {
              setSelectedColor(color);
              setMainImage(`${baseURL}/images/${image}`); // ✅ FIXED
            }}
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor:
                color.toLowerCase() === "blue" ? "#7a9ab8" :
                color.toLowerCase() === "gray" ? "#808080" :
                color.toLowerCase() === "white" ? "#FFFFFF" :
                color.toLowerCase() === "black" ? "#000000" : color,
              border: '2px solid',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            title={`Price: ₹${price}`}
          ></div>
        )
      ))}
    </div>
  </div>
)}

            

{pageLayoutError && <small style={{ color: 'red' }}>{pageLayoutError}</small>}
            {coverError && <small style={{ color: 'red' }}>{coverError}</small>}

            <div className='form-group row mt-3'>
              <div className='col-sm-4'><label>Quantity:</label></div>
              <div className='col-sm-8'>
                <input 
                  type="number" 
                  className='form-control' 
                  value={quantity} 
                  min={1}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setQuantity(value);
                    setQuantityError(value < 100 ? 'Minimum quantity is 100' : '');
                  }}
                />
                {quantityError && <small style={{ color: 'red' }}>{quantityError}</small>}
              </div>
            </div>

            <h4 className='mt-4'>₹ {finalPrice}/-</h4>
            <hr />

            {/* File Upload */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '20px' }}>
              <div style={{
                  width: '50%',
                  textAlign: 'center',
                  padding: '10px',
                  boxShadow: 'rgba(0,0,0,0.1) 0px 4px 6px',
                  borderRadius: '10px',
                  margin: '10px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}>
                <label htmlFor="fileUpload" style={{ cursor: 'pointer' }}>
                  <img src={`${baseURL}/images/upload.svg`}  alt="Upload" style={{ width: '100%' }} />
                  <p className="font-bold text-left mt-2">Upload a full design</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    <li>✶ Have a complete design</li>
                    <li>✶ Have your own designer</li>
                  </ul>
                </label>
                <input type="file" id="fileUpload" multiple style={{ display: 'none' }} onChange={handleFileChange} accept="image/*" />
              </div>
            </div>

            {/* Uploaded previews */}
            {previews.length > 0 && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {previews.map((src, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <img src={src} alt={`upload-${index}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '5px' }} />
                    <button type="button" onClick={() => handleRemoveFile(index)} style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      background: 'red',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}>×</button>
                  </div>
                ))}
              </div>
            )}

            <hr />
            <button type='button' className='btn mt-2' style={{ background: '#f5d148' }} onClick={handleSubmit}>ADD TO CART</button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Product_Preview;